import JsBarcode from "jsbarcode";
import CompanyLogoForm from "react-lib/apps/HISV3/common/CompanyLogoForm";
import { formatComma } from "react-lib/utils/tsUtils";
import { getLogoReportNResize } from "../common/CommonInterface";
import { truncateTextSimple } from "../common/HeaderPdfFormTemplate";

const FORM_NAME = "FormSupplyReport";

// Types
type FormSupplyReportProps = {
  // patientData
  hn: string;
  patientName: string;
  encounter: string;
  coverage: string;
  birthdate: string;
  fullAge: string;
  startEncounter: string;
  divisionName: string;
  doctorName: string;
  // supplyDetail
  supplyCode: string;
  supplyType: string;
  supplyStatus: string;
  supplyOrderTime: string;
  supplyOrderDivision: string;
  supplyEditUser: string;
  supplyPerformDivision: string;
  // supplyData
  supplyItem: Record<string, any>[];
  priceSummary: {
    total: string;
    claimable: string;
    nonClaimable: string;
  };
};

const FormSupplyReport = async (props: FormSupplyReportProps) => {
  const companyLogoForm = await CompanyLogoForm({
    font: "THSarabunNew",
    height: 40,
    form: FORM_NAME,
  });
  const { font, images } = companyLogoForm;

  const convertTextToBase64BarcodeBlob = (text: string) => {
    const canvas = document.createElement("canvas");

    JsBarcode(canvas, text, { displayValue: false });

    return canvas.toDataURL("image/png");
  };

  const supplyDetail = (label: string, value: string) => ({
    text: [{ text: `${label}: `, bold: true }, { text: value }],
    fontSize: 11,
    lineHeight: 0.7,
    margin: [0, 1, 0, 1],
  });

  const patientDetail = (label: string, value: string) => ({
    text: [{ text: `${label}: `, bold: true }, { text: value }],
  });

  const supplyData = () => {
    return props.supplyItem.map((item) => {
      return [
        {
          text: item?.no || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
        {
          text: item?.code || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
        {
          text: item?.name || "",
          border: [false, false, false, false],
        },
        {
          text: item?.quantity_request || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
        {
          text: formatComma(item?.price_total) || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
        {
          text: formatComma(item?.price_claimable) || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
        {
          text: formatComma(item?.price_non_claimable) || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
      ];
    });
  };

  const logoHeight = 36;
  const logoResize = await getLogoReportNResize(logoHeight, 1, FORM_NAME);

  return {
    defaultStyle: {
      font: font,
      fontSize: 14,
    },
    pageSize: "A5",
    pageOrientation: "landscape",
    pageMargins: [5, 168, 5, 40],
    header: function (currentPage: any) {
      return {
        margin: [5, 5, 5, 0],
        stack: [
          {
            margin: [0, 0, 0, 0],
            table: {
              widths: ["*", 150],
              body: [
                [
                  {
                    columns: [
                      {
                        image: "logo",
                        height: logoHeight,
                        width: logoResize.width,
                        border: [false, false, false, false],
                        alignment: "left",
                        margin: [0, 0, 0, 0],
                      },
                      {
                        stack: [
                          {
                            text: "โรงพยาบาลคณะทันตแพทย์ศาสตร์",
                            style: {
                              bold: true,
                              fontSize: 10,
                            },
                          },
                          {
                            text: "Chulalongkorn University Faculty of Dentistry",
                            style: {
                              bold: true,
                              fontSize: 10,
                            },
                          },
                          {
                            text: "ถนนอังรีดูนังต์ ปทุมวัน กรุงเทพฯ 10330 โทร. 02-218-8705",
                            style: {
                              fontSize: 10,
                            },
                          },
                        ],
                      },
                    ],
                    border: [false, false, false, false],
                  },
                  {
                    stack: [
                      supplyDetail("เลขที่ใบสั่งจ่ายเวชภัณฑ์", props.supplyCode || ""),
                      {
                        image: convertTextToBase64BarcodeBlob(props.supplyCode || ""),
                        width: 120,
                        height: 25,
                        margin: [0, 3, 0, 0],
                      },
                      supplyDetail("ประเภท", props.supplyType || ""),
                      supplyDetail("สถานะ", props.supplyStatus || ""),
                      supplyDetail("วันที่สั่ง", props.supplyOrderTime || ""),
                      supplyDetail("หน่วยงานขอเบิก", props.supplyOrderDivision || ""),
                      supplyDetail("ผู้สั่ง", props.supplyEditUser || ""),
                    ],
                    border: [false, false, false, false],
                    rowSpan: 3,
                  },
                ],
                [
                  {
                    text: `ใบสั่งจ่ายเวชภัณฑ์`,
                    style: {
                      bold: true,
                      fontSize: 20,
                      alignment: "right",
                    },
                    margin: [0, 10, 60, 0],
                    border: [false, false, false, false],
                  },
                  {},
                ],
                [
                  {
                    columns: [
                      {
                        width: 80,
                        stack: [patientDetail("HN", props.hn || "")],
                        margin: [0, 0, 20, 0],
                      },
                      {
                        width: 200,
                        stack: [patientDetail("ชื่อผู้ป่วย", props.patientName || "")],
                        margin: [0, 0, 20, 0],
                      },
                      {
                        width: "*",
                        stack: [patientDetail("Encounter", props.encounter || "")],
                      },
                    ],
                    margin: [0, 0, 0, 0],
                    border: [false, false, false, false],
                  },
                  {},
                ],
              ],
            },
          },
          {
            margin: [0, -2, 0, 0],
            table: {
              widths: [270, "*"],
              body: [
                [
                  {
                    stack: [
                      patientDetail("สิทธิ", truncateTextSimple(props.coverage, 480, 14) || ""),
                      patientDetail("วัน/เดือน/ปีเกิด", props.birthdate || ""),
                      patientDetail("อายุ", props.fullAge || ""),
                    ],
                    margin: [0, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    stack: [
                      patientDetail("วันที่รับบริการ", props.startEncounter || ""),
                      patientDetail("แผนก", props.divisionName || ""),
                      patientDetail("แพทย์", props.doctorName || ""),
                    ],
                    margin: [0, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                ],
              ],
            },
            layout: {
              hLineStyle: function (i: number, _node: any) {
                return { dash: { length: 2, space: 2 } };
              },
              hLineWidth: function (i: number, _node: any) {
                return 0.5;
              },
              paddingTop: () => 0,
              paddingBottom: () => 0,
            },
          },
        ],
      };
    },
    content: [
      {
        margin: [0, 0, 0, 0],
        table: {
          headerRows: 1,
          widths: [30, 50, "*", 40, 60, 60, 60],
          body: [
            [
              {
                text: "ลำดับ",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "code",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "รายการ",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "จำนวน",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "ราคารวม",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "เบิกได้",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "เบิกไม่ได้",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
            ],
            ...supplyData(),
          ],
        },
      },
    ],
    footer: {
      margin: [5, 0, 5, 0],
      table: {
        widths: ["*", 100, 60, 60, 60],
        body: [
          [
            {
              columns: [
                {
                  width: 130,
                  text: "กรุณาติดต่อหน่วยงานที่จ่าย",
                  bold: true,
                },
                {
                  width: "*",
                  text: props.supplyPerformDivision || "",
                },
              ],
              margin: [15, 0, 0, 0],
              border: [false, false, false, false],
            },

            {
              text: "รวม (บาท)",
              style: {
                bold: true,
              },
              margin: [0, 0, 0, 0],
              border: [false, false, false, false],
            },
            {
              text: formatComma(props.priceSummary?.total) || "0.00",
              style: {
                alignment: "center",
                bold: true,
              },
              margin: [0, 0, 0, 5],
              border: [false, false, false, false],
            },
            {
              text: formatComma(props.priceSummary?.claimable) || "0.00",
              style: {
                alignment: "center",
                bold: true,
              },
              margin: [0, 0, 0, 5],
              border: [false, false, false, false],
            },
            {
              text: formatComma(props.priceSummary?.nonClaimable) || "0.00",
              style: {
                alignment: "center",
                bold: true,
              },
              margin: [0, 0, 0, 5],
              border: [false, false, false, false],
            },
          ],
        ],
      },
    },
    images: {
      ...images,
    },
  };
};

export default FormSupplyReport;
